import type {
  ChessboardActionLogDto,
  ChessboardActionPayload,
  ChessboardDto,
  ChessMoveDto,
  CreateChessboardPayload,
  MakeMovePayload,
} from '@/core/data/chessboard/chessboard.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { firestore } from '@/core/network/firebase';
import { doc, onSnapshot, Timestamp, type Unsubscribe } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { ref } from 'vue';

interface ChessboardFirestore {
  id: string;
  updatedAt: Timestamp;
}

const logger = createLogger('ChessboardStore');

export const useChessboardStore = defineStore('chessboard', () => {
  const board = ref<ChessboardDto | null>(null);
  const boards = ref<ChessboardDto[]>([]);
  const moves = ref<ChessMoveDto[]>([]);
  const actionLogs = ref<ChessboardActionLogDto[]>([]);
  const isLoading = ref(false);
  const isError = ref(false);
  const isNotFound = ref(false);

  let unsubscribe: Unsubscribe | null = null;
  let didExist = false;
  let fetchBoardDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  function subscribe(id: string) {
    if (unsubscribe !== null) {
      unsubscribe();
      unsubscribe = null;
    }

    didExist = false;

    const docRef = doc(firestore, 'chessboards', id);

    unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          logger.info(`Chessboard ${id} Firestore doc found.`);
          didExist = true;
          const data = docSnap.data() as ChessboardFirestore;

          if (!board.value?.updatedAt || data.updatedAt.toDate() > new Date(board.value?.updatedAt)) {
            scheduleFetchBoard(docSnap.id);
          }
        } else if (didExist) {
          logger.info(`Chessboard ${id} Firestore doc deleted.`);
          board.value = null;
          isNotFound.value = true;
        } else {
          logger.warn(`Chessboard ${id} Firestore doc not found. Creating.`);
          gambits.chessboardService.createFirestoreDoc(id);
        }
      },
      (error) => {
        logger.error(`Chessboard ${id} onSnapshot listener error: ${error.message}`);
      },
    );
  }

  function scheduleFetchBoard(id: string) {
    if (fetchBoardDebounceTimer) {
      clearTimeout(fetchBoardDebounceTimer);
    }
    fetchBoardDebounceTimer = setTimeout(() => {
      fetchBoardDebounceTimer = null;
      void fetchBoard(id);
    }, 200);
  }

  async function fetchBoard(id: string) {
    const isRefresh = board.value?.id === id;

    if (!isRefresh) {
      isLoading.value = true;
      isError.value = false;
      isNotFound.value = false;
    }

    const result = await gambits.chessboardService.getBoard(id);

    if (!isRefresh) {
      isLoading.value = false;
    }

    if (result.data) {
      board.value = result.data;

      if (!isRefresh) {
        subscribe(board.value.id);
      }
    } else if (result.error?.code === 404) {
      // Abandoned and deleted boards both read as missing, so a stale link
      // lands on the same "no longer here" state either way.
      board.value = null;
      isNotFound.value = true;
    } else {
      isError.value = true;
    }

    return result;
  }

  async function fetchActiveByLocation(locationId: string) {
    isLoading.value = true;
    isError.value = false;

    const result = await gambits.chessboardService.getActiveByLocation(locationId);

    if (result.data) {
      boards.value = result.data;
    } else {
      boards.value = [];
      isError.value = true;
    }

    isLoading.value = false;
    return result;
  }

  async function fetchCompletedByLocation(locationId: string) {
    const result = await gambits.chessboardService.getCompletedByLocation(locationId);
    return result;
  }

  async function fetchMoves(chessboardId: string) {
    const result = await gambits.chessboardService.getMoves(chessboardId);

    if (result.data) {
      moves.value = result.data;
    }

    return result;
  }

  async function fetchActionLogs(chessboardId: string) {
    const result = await gambits.chessboardService.getActionLogs(chessboardId);

    if (result.data) {
      actionLogs.value = result.data;
    }

    return result;
  }

  async function create(payload: CreateChessboardPayload) {
    const result = await gambits.chessboardService.create(payload);

    if (result.data) {
      board.value = result.data;
    }

    return result;
  }

  async function makeMove(id: string, payload: MakeMovePayload) {
    const result = await gambits.chessboardService.makeMove(id, payload);

    if (result.data) {
      board.value = result.data;
    }

    return result;
  }

  async function performAction(id: string, payload: ChessboardActionPayload) {
    const result = await gambits.chessboardService.performAction(id, payload);

    if (result.data) {
      board.value = result.data;
    }

    return result;
  }

  function clear() {
    if (unsubscribe !== null) {
      unsubscribe();
      unsubscribe = null;
    }

    board.value = null;
    boards.value = [];
    moves.value = [];
    actionLogs.value = [];
    isLoading.value = false;
    isError.value = false;
    isNotFound.value = false;
  }

  return {
    board,
    boards,
    moves,
    actionLogs,
    isLoading,
    isError,
    isNotFound,
    fetchBoard,
    fetchActiveByLocation,
    fetchCompletedByLocation,
    fetchMoves,
    fetchActionLogs,
    create,
    makeMove,
    performAction,
    clear,
  };
});
